/**
 * Lightbinder talents (healer), rotation rework (docs/design/classes/lightbinder.md §5).
 * Infusion of Light, Divine Purpose and 2 Holy Shock charges are baseline now, so every node
 * changes a button or a proc of that kit. Tier 1: triage, mobility and Holy Shock. Tier 2: the
 * proc engine (Inflorescence, Divine Revelation) and utility. Tier 3: Glimmer, Purifying Light,
 * the Rising Sunlight transform, and three capstones: Last Rites (spot healing), Sunburst (double
 * Radiant Wave windows) and Avenging Crusader (Flash Heal → Hammer of Dawn inside the window).
 */
import { ABILITY, AURA, CLASS, CLASSES, PASSIVE } from '@keystone/content';
import type { AbilityDef, AuraDef, EffectSpec, ProcSpec } from '@keystone/sim';
import { auraRef } from '../../transforms';
import type { TalentNode } from '../../types';
import { injuredAllies, nodeFor, procPassive, resource } from './common';

const L = ABILITY.lightbinder;
const node = nodeFor(CLASS.lightbinder);
export const LIGHTBINDER_CAPSTONE = 'lightbinder.capstone';

/** Talent-only ability and aura ids (transform forms reach the class through addAbility). */
export const LIGHTBINDER_TALENT_IDS = {
  sunlitShock: 'lightbinder.sunlit_shock',
  sunlitShockReady: 'lightbinder.sunlit_shock_ready',
  sunlitEngine: 'talent.lightbinder.sunlit_engine',
  hammerOfDawn: 'lightbinder.hammer_of_dawn',
  hammerOfDawnReady: 'lightbinder.hammer_of_dawn_ready',
  crusader: 'talent.lightbinder.crusader',
} as const;
const T = LIGHTBINDER_TALENT_IDS;

const ifSelf = (auraId: string, then: EffectSpec[], otherwise?: EffectSpec[]): EffectSpec => ({
  type: 'conditional',
  cond: { type: 'selfHasAura', auraId },
  then,
  ...(otherwise ? { else: otherwise } : {}),
});
const gainRef = (auraId: string): EffectSpec => ({ type: 'applyAura', to: 'self', aura: auraRef(auraId) });
/** +1 Dawnlight inside Avenging Light (same shape as the class's own). */
const dawnlightInWindow: EffectSpec = ifSelf(AURA.avengingLight, [gainRef(AURA.dawnlight)]);

/** The class's proc engine, read from content so Holy Reverberation only changes Glorious Dawn's rate. */
const ENGINE: AuraDef = (() => {
  const def = CLASSES.find((c) => c.id === CLASS.lightbinder)?.passives?.find((p) => p.id === PASSIVE.lightEngine);
  if (!def) throw new Error('lightbinder: light engine passive missing from content');
  return def;
})();
const grantsGloriousDawn = (p: ProcSpec): boolean => p.effects.some((e) => e.type === 'applyAura' && e.aura.id === AURA.gloriousDawn);
/**
 * The engine's procs with Glorious Dawn at `chance`. Granted auras become `auraRef`s, so they resolve to
 * the class's current definitions (e.g. Inflorescence's 3-stack Infusion) whatever the node order.
 */
const engineProcsWithDawn = (chance: number): ProcSpec[] =>
  (ENGINE.procs ?? []).map((p) => ({
    ...p,
    ...(grantsGloriousDawn(p) ? { chance, badLuck: { step: chance / 2 } } : {}),
    effects: p.effects.map((e) => (e.type === 'applyAura' ? { ...e, aura: auraRef(e.aura.id) } : e)),
  }));

/** Lingering Light HoT; the Infused variant ticks twice as fast. */
const lingering = (id: string, intervalMs: number): EffectSpec => ({
  type: 'applyAura',
  aura: {
    id,
    name: 'Lingering Light',
    kind: 'buff',
    durationMs: 6000,
    stacking: 'refresh',
    pandemic: true,
    periodic: { intervalMs, hasteScaled: true, effects: [{ type: 'heal', amount: { coef: 0.4 } }] },
  },
});

// ---- Rising Sunlight: Holy Shock → Sunlit Shock ×3 ---------------------------------------
const sunlitShock: AbilityDef = {
  id: T.sunlitShock,
  name: 'Sunlit Shock',
  school: 'holy',
  description: 'A Holy Shock that echoes: heals an ally (or strikes an enemy), then repeats at 60% on the most injured other ally (or a second enemy within 10 yd). Generates 1 Holy Power.',
  targeting: 'any',
  range: 40,
  cost: [{ resource: 'mana', amount: 250 }],
  gain: [{ resource: 'holy_power', amount: 1 }],
  effects: [
    {
      type: 'conditional',
      cond: { type: 'targetIsEnemy' },
      then: [
        { type: 'damage', school: 'holy', amount: { coef: 1.8, variance: 0.05 } },
        { type: 'damage', school: 'holy', to: 'area', area: { anchor: 'target', shape: { type: 'circle', radius: 10 }, affects: 'enemies', maxTargets: 1, excludePrimary: true }, amount: { coef: 1.08, variance: 0.05 } },
      ],
      else: [
        { type: 'heal', amount: { coef: 3.5, variance: 0.05 } },
        { type: 'heal', to: 'area', area: { ...injuredAllies(40), excludePrimary: true }, smart: 1, amount: { coef: 2.1, variance: 0.05 } },
      ],
    },
    ifSelf(AURA.gloriousDawn, [resource('holy_power', 1)]),
  ],
  ui: { tags: ['heal', 'builder'], color: '#ffd166', animAs: L.holyShock },
};

const sunlitShockReady: AuraDef = {
  id: T.sunlitShockReady,
  name: 'Sunlit Shock',
  kind: 'buff',
  durationMs: 12000,
  stacking: 'stack',
  maxStacks: 3,
  overrides: [{ abilityId: L.holyShock, with: T.sunlitShock }],
  procs: [{ on: 'castResolved', abilityIds: [T.sunlitShock], consumeStack: true, effects: [] }],
  ui: { proc: true, stackCounterOn: [L.holyShock, T.sunlitShock] },
};

// ---- Avenging Crusader: Flash Heal → Hammer of Dawn inside the window -----------------------
const hammerOfDawn: AbilityDef = {
  id: T.hammerOfDawn,
  name: 'Hammer of Dawn',
  school: 'holy',
  description: 'Strike an enemy with holy light and heal the 3 most injured allies within 40 yd. Generates 1 Holy Power and Holy Shock recharges 1 s sooner.',
  targeting: 'enemy',
  range: 30,
  cost: [{ resource: 'mana', amount: 200 }],
  gain: [{ resource: 'holy_power', amount: 1 }],
  effects: [
    { type: 'damage', school: 'holy', amount: { coef: 2.5, variance: 0.05 } },
    { type: 'heal', to: 'area', area: injuredAllies(40), smart: 3, amount: { coef: 1.5, variance: 0.05 } },
    { type: 'modifyCooldown', to: 'self', abilityId: L.holyShock, deltaMs: -1000 },
  ],
  ui: { tags: ['builder', 'heal'], color: '#e0b84a', animAs: L.judgment },
};

const hammerOfDawnReady: AuraDef = {
  id: T.hammerOfDawnReady,
  name: 'Avenging Crusader',
  kind: 'buff',
  durationMs: 12000,
  stacking: 'refresh',
  overrides: [{ abilityId: L.flashHeal, with: T.hammerOfDawn }],
  ui: { proc: true },
};

export const LIGHTBINDER_TREE: readonly TalentNode[] = [
  // ---------------------------------------------------------------- tier 1
  node({
    id: 'lightbinder.t1.flash_hot',
    name: 'Lingering Light',
    description: 'Flash Heal leaves a 6 s heal-over-time on the target. After an Infused Flash Heal it ticks twice as fast.',
    tier: 1,
    col: 0,
    cost: 2,
    effect: [
      {
        op: 'addEffects',
        abilityId: L.flashHeal,
        effects: [ifSelf(AURA.infusionOfLight, [lingering('talent.lightbinder.lingering_light_infused', 1000)], [lingering('talent.lightbinder.lingering_light', 2000)])],
      },
    ],
  }),
  node({
    id: 'lightbinder.t1.swift_verdict',
    name: 'Swift Verdict',
    description: 'Judgment reaches 40 yd and no longer needs you to face the target.',
    tier: 1,
    col: 1,
    cost: 1,
    effect: [{ op: 'changeTargeting', abilityId: L.judgment, targeting: 'enemy', range: 40, requiresFacing: false }],
  }),
  node({
    id: 'lightbinder.t1.mobile_flash',
    name: 'Light in Motion',
    description: 'Flash Heal can be cast while moving, but costs 420 mana instead of 350.',
    tier: 1,
    col: 2,
    cost: 2,
    effect: [
      { op: 'patchAbility', abilityId: L.flashHeal, patch: { canCastWhileMoving: true } },
      { op: 'modifyResource', abilityId: L.flashHeal, field: 'cost', resource: 'mana', amount: 420 },
    ],
  }),
  node({
    id: 'lightbinder.t1.reverberation',
    name: 'Holy Reverberation',
    description: 'Holy Shock gains a 3rd charge, and Glorious Dawn procs more often (15% instead of 12%).',
    tier: 1,
    col: 3,
    cost: 3,
    effect: [
      { op: 'modifyCooldown', abilityId: L.holyShock, charges: 3 },
      { op: 'patchAura', auraId: PASSIVE.lightEngine, patch: { procs: engineProcsWithDawn(0.15) } },
    ],
  }),
  node({
    id: 'lightbinder.t1.judgment_of_light',
    name: 'Judgment of Light',
    description: 'Judgment also heals the most injured ally within 40 yd.',
    tier: 1,
    col: 4,
    cost: 2,
    effect: [{ op: 'addEffects', abilityId: L.judgment, effects: [{ type: 'heal', to: 'area', area: injuredAllies(40), smart: 1, amount: { coef: 1.5, variance: 0.05 } }] }],
  }),
  node({
    id: 'lightbinder.t1.steadfast_aegis',
    name: 'Steadfast Aegis',
    description: "Guardian's Aegis recharges in 60 s instead of 90 s.",
    tier: 1,
    col: 5,
    cost: 1,
    effect: [{ op: 'modifyCooldown', abilityId: L.guardiansAegis, durationMs: 60000 }],
  }),

  // ---------------------------------------------------------------- tier 2
  node({
    id: 'lightbinder.t2.word_of_radiance',
    name: 'Word of Radiance',
    description: 'Word of Light also heals the 3 most injured other allies within 15 yd of the target.',
    tier: 2,
    col: 0,
    cost: 3,
    effect: [
      {
        op: 'addEffects',
        abilityId: L.wordOfLight,
        effects: [{ type: 'heal', to: 'area', area: { anchor: 'target', shape: { type: 'circle', radius: 15 }, affects: 'allies', excludePrimary: true }, smart: 3, amount: { coef: 2.4, variance: 0.05 } }],
      },
    ],
  }),
  node({
    id: 'lightbinder.t2.inflorescence',
    name: 'Inflorescence',
    description: 'Infusion of Light stacks up to 3 times, and an Infused Flash Heal also grants 1 Holy Power.',
    tier: 2,
    col: 1,
    cost: 2,
    effect: [
      { op: 'patchAura', auraId: AURA.infusionOfLight, patch: { maxStacks: 3 } },
      { op: 'addEffects', abilityId: L.flashHeal, effects: [ifSelf(AURA.infusionOfLight, [resource('holy_power', 1)])] },
    ],
  }),
  node({
    id: 'lightbinder.t2.tidal_radiance',
    name: 'Tidal Radiance',
    description: 'Radiant Wave recharges in 20 s instead of 30 s.',
    tier: 2,
    col: 2,
    cost: 2,
    effect: [{ op: 'modifyCooldown', abilityId: L.radiantWave, durationMs: 20000 }],
  }),
  node({
    id: 'lightbinder.t2.aegis_of_light',
    name: 'Aegis of Light',
    description: "Guardian's Aegis also shields the target for 15% of their max HP.",
    tier: 2,
    col: 3,
    cost: 2,
    prereqs: ['lightbinder.t1.steadfast_aegis'],
    effect: [
      { op: 'addEffects', abilityId: L.guardiansAegis, effects: [{ type: 'absorb', auraId: 'talent.lightbinder.aegis_of_light', amount: { pctTargetMaxHp: 0.15 } }] },
      { op: 'registerAura', aura: { id: 'talent.lightbinder.aegis_of_light', name: 'Aegis of Light', kind: 'buff', durationMs: 8000, stacking: 'refresh', absorb: {} } },
    ],
  }),
  node({
    id: 'lightbinder.t2.divine_revelation',
    name: 'Divine Revelation',
    description: 'When Divine Purpose procs, Holy Shock gains a charge. A free spender inside Avenging Light grants 2 Dawnlight instead of 1.',
    tier: 2,
    col: 4,
    cost: 2,
    effect: [
      { op: 'patchAura', auraId: AURA.divinePurpose, patch: { onApply: [{ type: 'modifyCooldown', to: 'self', abilityId: L.holyShock, addCharge: true }] } },
      ...[L.wordOfLight, L.dawnsong].map((abilityId) => ({ op: 'addEffects' as const, abilityId, effects: [ifSelf(AURA.divinePurpose, [dawnlightInWindow])] })),
    ],
  }),
  node({
    id: 'lightbinder.t2.unshackled',
    name: 'Unshackled',
    description: 'Blessing of Freedom recharges in 15 s and also removes one Magic or Poison effect.',
    tier: 2,
    col: 5,
    cost: 1,
    effect: [
      { op: 'modifyCooldown', abilityId: L.blessingOfFreedom, durationMs: 15000 },
      { op: 'addEffects', abilityId: L.blessingOfFreedom, effects: [{ type: 'dispel', dispelTypes: ['magic', 'poison'], harmful: true, count: 1 }] },
    ],
  }),

  // ---------------------------------------------------------------- tier 3
  node({
    id: 'lightbinder.t3.glimmer',
    name: 'Glimmer of Light',
    description: 'Holy Shock on an enemy also heals the most injured ally within 40 yd.',
    tier: 3,
    col: 0,
    cost: 3,
    effect: [
      {
        op: 'addEffects',
        abilityId: L.holyShock,
        effects: [{ type: 'conditional', cond: { type: 'targetIsEnemy' }, then: [{ type: 'heal', to: 'area', area: injuredAllies(40), smart: 1, amount: { coef: 2.5, variance: 0.05 } }] }],
      },
    ],
  }),
  node({
    // Id kept from the old Purifying Wave node (saved loadouts keep it); it now also carries Purifying Touch.
    id: 'lightbinder.t3.purifying_wave',
    name: 'Purifying Light',
    description: 'Radiant Wave also removes one Magic or Poison effect from every ally it heals, and Cleanse also heals the target.',
    tier: 3,
    col: 1,
    cost: 2,
    effect: [
      {
        op: 'addEffects',
        abilityId: L.radiantWave,
        effects: [{ type: 'dispel', to: 'area', area: { anchor: 'caster', shape: { type: 'circle', radius: 40 }, affects: 'allies' }, dispelTypes: ['magic', 'poison'], harmful: true, count: 1 }],
      },
      { op: 'addEffects', abilityId: L.cleanse, effects: [{ type: 'heal', amount: { coef: 2.0, variance: 0.05 } }] },
    ],
  }),
  node({
    id: 'lightbinder.t3.rising_sunlight',
    name: 'Rising Sunlight',
    description: 'Dawnbringer turns your next 3 Holy Shocks into Sunlit Shock (12 s), which echoes onto a second ally or enemy at 60% strength.',
    tier: 3,
    col: 2,
    cost: 3,
    effect: [
      { op: 'addAbility', ability: sunlitShock },
      { op: 'addEffects', abilityId: L.dawnbringer, effects: [{ type: 'applyAura', to: 'self', stacks: 3, aura: sunlitShockReady }] },
      // Sunlit Shock is a Holy Shock for the proc engine, Glorious Dawn and Avenging Light.
      {
        op: 'addPassive',
        aura: {
          id: T.sunlitEngine,
          name: 'Rising Sunlight',
          kind: 'buff',
          durationMs: 0,
          stacking: 'unique',
          flags: { hidden: true, persistThroughDeath: true },
          procs: [
            { on: 'castResolved', abilityIds: [T.sunlitShock], if: { type: 'selfHasAura', auraId: AURA.avengingLight }, effects: [gainRef(AURA.infusionOfLight)] },
            { on: 'castResolved', abilityIds: [T.sunlitShock], if: { type: 'selfLacksAura', auraId: AURA.avengingLight }, chance: 0.25, badLuck: { step: 0.125 }, effects: [gainRef(AURA.infusionOfLight)] },
            { on: 'castResolved', abilityIds: [T.sunlitShock], chance: 0.12, badLuck: { step: 0.06 }, effects: [gainRef(AURA.gloriousDawn)] },
          ],
        },
      },
      {
        op: 'addAuraMods',
        auraId: AURA.gloriousDawn,
        mods: [
          { stat: 'healingDonePct', value: 0.3, abilityIds: [T.sunlitShock] },
          { stat: 'damageDonePct', value: 0.3, abilityIds: [T.sunlitShock] },
        ],
      },
      { op: 'addProc', auraId: AURA.gloriousDawn, proc: { on: 'castResolved', abilityIds: [T.sunlitShock], consumeStack: true, effects: [] } },
      { op: 'addAuraMods', auraId: AURA.avengingLight, mods: [{ stat: 'critChanceBonus', value: 0.3, abilityIds: [T.sunlitShock] }] },
    ],
  }),
  node({
    id: 'lightbinder.t3.last_rites',
    name: 'Last Rites',
    description: 'Capstone. Flash Heal, Holy Shock and Word of Light heal an ally who is still below 50% health again for a large amount.',
    tier: 3,
    col: 3,
    cost: 3,
    choiceGroup: LIGHTBINDER_CAPSTONE,
    effect: [
      procPassive('talent.lightbinder.last_rites', 'Last Rites', {
        on: 'healDealt',
        abilityIds: [L.flashHeal, L.holyShock, L.wordOfLight],
        effects: [{ type: 'heal', if: { type: 'targetHpBelow', pct: 0.5 }, amount: { coef: 2.0, variance: 0.05 } }],
      }),
    ],
  }),
  node({
    id: 'lightbinder.t3.sunburst',
    name: 'Sunburst',
    description: 'Capstone. Radiant Wave has 2 charges and its heal-over-time lasts 10 s. Each Radiant Wave inside Avenging Light grants 2 Dawnlight.',
    tier: 3,
    col: 4,
    cost: 3,
    choiceGroup: LIGHTBINDER_CAPSTONE,
    effect: [
      { op: 'modifyCooldown', abilityId: L.radiantWave, charges: 2 },
      { op: 'patchAura', auraId: AURA.radiantWaveHot, patch: { durationMs: 10000 } },
      { op: 'addEffects', abilityId: L.radiantWave, effects: [dawnlightInWindow] },
    ],
  }),
  node({
    id: 'lightbinder.t3.crusader',
    name: 'Avenging Crusader',
    description:
      'Capstone. Dawnbringer also starts Avenging Crusader (12 s): Flash Heal becomes Hammer of Dawn, an instant strike that heals 3 allies, and Judgment and Holy Shock damage heal the 2 most injured allies.',
    tier: 3,
    col: 5,
    cost: 3,
    choiceGroup: LIGHTBINDER_CAPSTONE,
    effect: [
      { op: 'addAbility', ability: hammerOfDawn },
      { op: 'addEffects', abilityId: L.dawnbringer, effects: [{ type: 'applyAura', to: 'self', aura: hammerOfDawnReady }] },
      procPassive(T.crusader, 'Avenging Crusader', {
        on: 'damageDealt',
        abilityIds: [L.judgment, L.holyShock],
        if: { type: 'selfHasAura', auraId: T.hammerOfDawnReady },
        effects: [{ type: 'heal', to: 'area', area: injuredAllies(40), smart: 2, amount: { coef: 1.6, variance: 0.05 } }],
      }),
    ],
  }),
];
